/**
 * /quotes/:quoteId: build, price and send one quote (docs/10-quote-engine.md §7).
 *
 *   Home & heat loss   the property answers (from the EPC), the RdSAP heat loss with
 *                      its element-by-element breakdown, the installer's survey
 *                      override, occupants, flow temperature and the grant check
 *   Options & pricing  every package priced for this home: the heat pump and
 *                      cylinder chosen, each cost line with its sell price, VAT,
 *                      grant, profit and margin; the recommended option, this
 *                      quote's markup, a discount, extras and one-off lines
 *   Proposal & send    the customer's view (preview), the email, the link, and
 *                      what the customer has done with it
 *
 * Edits are local until "Save and recalculate": the API recalculates everything
 * (it never trusts a price from the browser) and returns the quote.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, Check, Copy, ExternalLink, Eye, FileText, Mail, Plus, RefreshCw, Save, Send, Trash2, X } from "lucide-react";
import type { FullProperty } from "@widget";
import { useBreadcrumbLabel } from "../../breadcrumbs";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { useQuotesApi, type PricedOption, type Product, type Proposal, type Quote, type QuoteSettings, type Selection, type Survey } from "../../quotes/api";
import { completeProperty, gbp, label, pct, when, FUELS } from "../../quotes/format";
import { PropertyForm } from "../../quotes/PropertyForm";
import { ProposalView } from "../../quotes/ProposalView";
import { QuoteStatusChip } from "./QuotesPage";
import { QuoteTermsCard, useQuoteAccess } from "../../quotes/Access";
import "../../quotes/quotes.css";

type Tab = "home" | "options" | "send";
type Draft = Pick<Quote, "customer" | "address" | "survey" | "selection" | "content">;

const draftOf = (q: Quote): Draft => ({
  customer: q.customer,
  address: q.address,
  // The engine's result is the server's: not part of the edit
  survey: { ...q.survey, result: undefined },
  selection: q.selection,
  content: q.content,
});

export function QuoteBuilderPage() {
  const { quoteId = "" } = useParams();
  const api = useQuotesApi();
  const navigate = useNavigate();
  const [quote, setQuote] = useState<Quote | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [settings, setSettings] = useState<QuoteSettings | null>(null);
  const [products, setProducts] = useState<Record<string, Product>>({});
  const [tab, setTab] = useState<Tab>("options");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger" | "info"; text: string } | null>(null);
  const [preview, setPreview] = useState<Proposal | null>(null);
  const [confirm, setConfirm] = useState<null | "delete" | "withdraw" | "accepted" | "declined">(null);
  const [busy, setBusy] = useState(false);
  const [statusNote, setStatusNote] = useState("");

  useBreadcrumbLabel(quoteId, quote?.reference);

  const loaded = useCallback((q: Quote) => {
    setQuote(q);
    setDraft(draftOf(q));
  }, []);

  useEffect(() => {
    let live = true;
    Promise.all([api.getQuote(quoteId), api.getSettings(), api.listProducts()])
      .then(([q, s, ps]) => {
        if (!live) return;
        loaded(q);
        setSettings(s.settings);
        setProducts(Object.fromEntries(ps.map((p) => [p.productId, p])));
        // A new quote opens on its options; a quote with no heat loss on the home tab
        if (!q.computed.designKw) setTab("home");
      })
      .catch((e: Error) => live && setMessage({ tone: "danger", text: e.message }));
    return () => {
      live = false;
    };
  }, [api, quoteId, loaded]);

  const dirty = useMemo(() => quote !== null && draft !== null && JSON.stringify(draftOf(quote)) !== JSON.stringify(draft), [quote, draft]);
  const answered = quote?.status === "accepted" || quote?.status === "declined";

  // Warn before leaving with unsaved edits
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  async function save(refresh = false) {
    if (!draft) return;
    setSaving(true);
    setMessage(null);
    try {
      const { survey, ...rest } = draft;
      const { result: _r, ...surveyInputs } = survey;
      void _r;
      loaded(await api.updateQuote(quoteId, { ...rest, survey: surveyInputs as Survey, refresh }));
      setMessage({ tone: "success", text: refresh ? "Heat loss and prices recalculated." : "Saved and recalculated." });
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  async function run(action: () => Promise<Quote | void>, done?: string) {
    setBusy(true);
    setMessage(null);
    try {
      const q = await action();
      if (q) loaded(q);
      if (done) setMessage({ tone: "success", text: done });
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  }

  async function openPreview() {
    if (dirty) await save();
    try {
      setPreview(await api.previewQuote(quoteId));
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    }
  }

  if (!quote || !draft || !settings) {
    return <div className="page">{message ? <div className={`flash flash-${message.tone}`}>{message.text}</div> : <p className="muted">Loading…</p>}</div>;
  }

  const setSurvey = (patch: Partial<Survey>) => setDraft((d) => (d ? { ...d, survey: { ...d.survey, ...patch } } : d));
  const setSelection = (patch: Partial<Selection>) => setDraft((d) => (d ? { ...d, selection: { ...d.selection, ...patch } } : d));

  return (
    <div className="page q-page q-builder">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="q-builder-head">
        <div>
          <div className="q-builder-title">
            <h1>{quote.reference}</h1>
            <QuoteStatusChip status={quote.status} changed={quote.changedSinceSent} />
          </div>
          <p className="muted">
            {quote.customer.name || "No customer name yet"} · {quote.address.line}
          </p>
        </div>
        <div className="q-builder-actions">
          {!answered && (
            <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={!dirty || saving}>
              <Save size={16} aria-hidden="true" /> {saving ? "Recalculating…" : "Save and recalculate"}
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={() => void openPreview()}>
            <Eye size={16} aria-hidden="true" /> Preview
          </button>
          {!answered && (
            <button type="button" className="btn btn-accent" onClick={() => setTab("send")}>
              <Send size={16} aria-hidden="true" /> Send
            </button>
          )}
        </div>
      </div>

      {message && <div className={`flash flash-${message.tone}`}>{message.text}</div>}
      {dirty && !answered && (
        <div className="flash flash-info">
          <RefreshCw size={16} aria-hidden="true" /> Unsaved changes: save to recalculate the heat loss and prices.
        </div>
      )}
      {quote.changedSinceSent && !answered && <div className="flash flash-info">You've changed this quote since it was sent. The customer still sees the sent version until you send it again.</div>}
      {answered && <AnsweredCard quote={quote} />}

      <div className="ed-segmented q-tabs" role="tablist">
        {(
          [
            ["home", "Home & heat loss"],
            ["options", "Options & pricing"],
            ["send", "Proposal & send"],
          ] as [Tab, string][]
        ).map(([t, l]) => (
          <button key={t} type="button" role="tab" aria-selected={tab === t} className={tab === t ? "is-on" : ""} onClick={() => setTab(t)}>
            {l}
          </button>
        ))}
      </div>

      <fieldset className="q-fieldset" disabled={answered}>
        {tab === "home" && <HomeTab quote={quote} draft={draft} setDraft={setDraft} setSurvey={setSurvey} onRefresh={() => void save(true)} saving={saving} />}
        {tab === "options" && <OptionsTab quote={quote} draft={draft} settings={settings} products={products} setSelection={setSelection} dirty={dirty} />}
      </fieldset>
      {tab === "send" && (
        <SendTab
          quote={quote}
          draft={draft}
          settings={settings}
          dirty={dirty}
          busy={busy}
          answered={answered}
          setDraft={setDraft}
          onSend={(body) => run(() => api.sendQuote(quoteId, body), body.email ? `Quote emailed to ${body.to || quote.customer.email}.` : "Link published: copy it below.")}
          onPreview={() => void openPreview()}
        />
      )}

      {/* ── Less common actions ───────────────────────────────────── */}
      <div className="q-more">
        <button type="button" className="link-button small" onClick={() => void run(async () => navigate(`/quotes/${(await api.duplicateQuote(quoteId)).quoteId}`))}>
          <Copy size={14} aria-hidden="true" /> Duplicate as a new quote
        </button>
        {(quote.status === "sent" || quote.status === "viewed") && (
          <button type="button" className="link-button small" onClick={() => setConfirm("withdraw")}>
            <X size={14} aria-hidden="true" /> Withdraw the link
          </button>
        )}
        {!answered && (
          <>
            <button type="button" className="link-button small" onClick={() => setConfirm("accepted")}>
              <Check size={14} aria-hidden="true" /> Mark as accepted (by phone or paper)
            </button>
            <button type="button" className="link-button small" onClick={() => setConfirm("declined")}>
              <X size={14} aria-hidden="true" /> Mark as declined
            </button>
          </>
        )}
        <button type="button" className="link-button small q-danger" onClick={() => setConfirm("delete")}>
          <Trash2 size={14} aria-hidden="true" /> Delete quote
        </button>
      </div>

      <ConfirmDialog
        open={confirm === "delete"}
        title={`Delete ${quote.reference}?`}
        confirmLabel="Delete quote"
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void run(async () => (await api.deleteQuote(quoteId), navigate("/quotes", { replace: true })))}
      >
        <p>The quote and its customer link are deleted. This can't be undone.</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm === "withdraw"}
        title="Withdraw the link?"
        confirmLabel="Withdraw"
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void run(() => api.withdrawQuote(quoteId), "Link withdrawn: the quote is a draft again.")}
      >
        <p>The customer's link stops working and the quote goes back to draft. Sending it again creates a new link.</p>
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm === "accepted" || confirm === "declined"}
        title={confirm === "accepted" ? "Mark as accepted?" : "Mark as declined?"}
        confirmLabel={confirm === "accepted" ? "Mark accepted" : "Mark declined"}
        danger={confirm === "declined"}
        busy={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void run(() => api.setStatus(quoteId, { status: confirm as "accepted" | "declined", note: statusNote, optionId: quote.computed.recommendedId ?? undefined }))}
      >
        <p>{confirm === "accepted" ? `Records the recommended option as accepted by ${quote.customer.name || "the customer"}.` : "Records that the customer declined."}</p>
        <textarea className="input" rows={2} placeholder="Note (optional)" value={statusNote} onChange={(e) => setStatusNote(e.target.value)} />
      </ConfirmDialog>

      {preview && (
        <div className="q-preview" role="dialog" aria-modal="true" aria-label="Customer preview">
          <div className="q-preview-bar">
            <strong>Customer preview</strong>
            <button type="button" className="btn btn-secondary" onClick={() => setPreview(null)}>
              <X size={16} aria-hidden="true" /> Close
            </button>
          </div>
          <div className="q-preview-body">
            <ProposalView proposal={preview} preview />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Answered ────────────────────────────────────────────────────────────────

function AnsweredCard({ quote }: { quote: Quote }) {
  if (quote.status === "declined") {
    return (
      <div className="card q-answer q-answer-declined">
        <h2>Declined {when(quote.decline?.at)}</h2>
        <p>{quote.decline?.reason || "No reason given."}</p>
      </div>
    );
  }
  const a = quote.acceptance;
  if (!a) return null;
  return (
    <div className="card q-answer">
      <h2>
        <Check size={20} aria-hidden="true" /> Accepted {when(a.signedAt)}
      </h2>
      <dl className="facts">
        <dt>Option</dt>
        <dd>{a.optionName}</dd>
        <dt>Extras</dt>
        <dd>{a.extras.length ? a.extras.map((x) => `${x.name}${x.qty > 1 ? ` × ${x.qty}` : ""}`).join(", ") : "None"}</dd>
        <dt>Total</dt>
        <dd>
          {gbp(a.totals.gross)} inc VAT · grant {gbp(a.totals.grant)} · <strong>{gbp(a.totals.payable)} to pay</strong>
        </dd>
        <dt>Payment</dt>
        <dd>{a.payment === "finance" ? `Finance over ${a.financeMonths} months` : "In full"}</dd>
        <dt>Signed</dt>
        <dd>
          “{a.name}”{a.offline ? ` (recorded in the console${a.note ? `: ${a.note}` : ""})` : ` from ${a.ip ?? "?"}`}
        </dd>
      </dl>
    </div>
  );
}

// ─── Home & heat loss ────────────────────────────────────────────────────────

function HomeTab({
  quote,
  draft,
  setDraft,
  setSurvey,
  onRefresh,
  saving,
}: {
  quote: Quote;
  draft: Draft;
  setDraft: (fn: (d: Draft | null) => Draft | null) => void;
  setSurvey: (p: Partial<Survey>) => void;
  onRefresh: () => void;
  saving: boolean;
}) {
  const result = quote.survey.result;
  const hl = result?.heatLoss;
  const s = draft.survey;
  const property = s.property ?? completeProperty({});
  const g = result?.grant;
  return (
    <div className="q-grid">
      <section className="card">
        <h2>Heat loss</h2>
        <div className="q-hl-top">
          <div className="q-big">
            <span>{quote.computed.designKw ?? "—"}</span> kW
          </div>
          <div className="muted">
            {s.heatLossOverrideKw
              ? `Your survey figure${hl ? ` (the RdSAP estimate is ${hl.kw} kW, level ${hl.level})` : ""}`
              : hl
                ? `RdSAP 10 estimate, level ${hl.level}: ${hl.method}. Design ${hl.externalC} °C outside, ${hl.internalC} °C inside (${hl.designLocation}).`
                : "No estimate: enter your survey figure"}
          </div>
        </div>
        {result?.noEstimate && !s.heatLossOverrideKw && (
          <div className="flash flash-info">
            <AlertTriangle size={16} aria-hidden="true" /> We can't estimate this home online (no usable certificate or similar homes nearby). Enter the heat loss from your survey.
          </div>
        )}
        <div className="q-row-fields">
          <div className="ed-field">
            <label className="ed-label" htmlFor="q-override">
              Survey heat loss (kW)
            </label>
            <input
              id="q-override"
              className="input"
              type="number"
              step="0.1"
              min="0.5"
              placeholder={hl ? `Estimate: ${hl.kw}` : "e.g. 7.4"}
              value={s.heatLossOverrideKw ?? ""}
              onChange={(e) => setSurvey({ heatLossOverrideKw: e.target.value ? Number(e.target.value) : null })}
            />
            <p className="ed-hint">From your room-by-room survey (MCS MIS 3005-D). Leave blank to use the estimate.</p>
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="q-override-note">
              Survey note (shown to the customer)
            </label>
            <input id="q-override-note" className="input" value={s.overrideNote} maxLength={300} onChange={(e) => setSurvey({ overrideNote: e.target.value })} placeholder="e.g. room-by-room survey on 3 Oct" />
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="q-occupants">
              Occupants
            </label>
            <input id="q-occupants" className="input" type="number" min={1} max={20} placeholder={`${quote.computed.occupants} (bedrooms + 1)`} value={s.occupants ?? ""} onChange={(e) => setSurvey({ occupants: e.target.value ? Number(e.target.value) : null })} />
            <p className="ed-hint">Sizes the cylinder: needs {quote.computed.requiredCylinderL} L.</p>
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="q-flow">
              Design flow temperature
            </label>
            <select id="q-flow" className="input" value={s.flowTempC ?? ""} onChange={(e) => setSurvey({ flowTempC: e.target.value ? Number(e.target.value) : null })}>
              <option value="">Your default</option>
              {[35, 40, 45, 50, 55].map((t) => (
                <option key={t} value={t}>
                  {t} °C
                </option>
              ))}
            </select>
            <p className="ed-hint">Sets the SCOP used for running costs.</p>
          </div>
        </div>
        {hl && hl.elements.length > 0 && (
          <details className="q-details">
            <summary>Element-by-element breakdown ({hl.totalWPerK} W/K)</summary>
            <div className="table-scroll">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th>Element</th>
                    <th className="num">Area m²</th>
                    <th className="num">U W/m²K</th>
                    <th className="num">W/K</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {hl.elements.map((e, i) => (
                    <tr key={i}>
                      <td>{e.element}</td>
                      <td className="num">{e.area ?? ""}</td>
                      <td className="num">{e.u ?? ""}</td>
                      <td className="num">{e.wPerK}</td>
                      <td className="muted">{e.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        )}
        <button type="button" className="link-button small" onClick={onRefresh} disabled={saving}>
          <RefreshCw size={14} aria-hidden="true" /> Look up the EPC again and recalculate
        </button>
      </section>

      <section className="card">
        <h2>The home</h2>
        <p className="card-sub">
          {result?.certificate ? `EPC ${result.certificate.current.band ?? ""}, certificate ${result.certificate.number}.` : "No EPC certificate used."} Changing these recalculates the heat loss.
        </p>
        <PropertyForm value={property} onChange={(p: FullProperty) => setSurvey({ property: p })} />
      </section>

      <section className="card">
        <h2>Boiler Upgrade Scheme</h2>
        <label className="ed-toggle q-toggle-row">
          <input type="checkbox" role="switch" checked={s.includeGrant} onChange={(e) => setSurvey({ includeGrant: e.target.checked })} />
          <span className="ed-toggle-track" aria-hidden="true" />
          Take the grant off the price ({g ? (g.eligible ? gbp(g.grant) : "not eligible on these answers") : "—"})
        </label>
        <div className="q-checks-grid">
          {(
            [
              ["owner", "The customer owns the home"],
              ["newBuild", "It's a new build"],
              ["previousFunding", "It had heat pump funding before"],
            ] as ["owner" | "newBuild" | "previousFunding", string][]
          ).map(([k, l]) => (
            <label key={k} className="q-check">
              <input type="checkbox" checked={s.grantAnswers[k]} onChange={(e) => setSurvey({ grantAnswers: { ...s.grantAnswers, [k]: e.target.checked } })} /> {l}
            </label>
          ))}
        </div>
        {g && (
          <ul className="q-grant-checks">
            {g.checks.map((c) => (
              <li key={c.key} className={`is-${c.status}`}>
                {c.status === "pass" ? <Check size={14} /> : c.status === "fail" ? <X size={14} /> : <FileText size={14} />} {c.detail}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="card">
        <h2>Customer</h2>
        <div className="q-customer-grid">
          {(["name", "email", "phone"] as const).map((k) => (
            <div key={k} className="ed-field">
              <label className="ed-label" htmlFor={`qc-${k}`}>
                {k === "name" ? "Name" : k === "email" ? "Email" : "Phone"}
              </label>
              <input id={`qc-${k}`} className="input" value={draft.customer[k]} onChange={(e) => setDraft((d) => (d ? { ...d, customer: { ...d.customer, [k]: e.target.value } } : d))} />
            </div>
          ))}
        </div>
        <div className="ed-field">
          <label className="ed-label" htmlFor="qc-address">
            Address
          </label>
          <input id="qc-address" className="input" value={draft.address.line} onChange={(e) => setDraft((d) => (d ? { ...d, address: { ...d.address, line: e.target.value } } : d))} />
        </div>
        {quote.source?.widgetId && <p className="muted">Started from a lead on one of your website components.</p>}
      </section>
    </div>
  );
}

// ─── Options & pricing ───────────────────────────────────────────────────────

function OptionsTab({
  quote,
  draft,
  settings,
  products,
  setSelection,
  dirty,
}: {
  quote: Quote;
  draft: Draft;
  settings: QuoteSettings;
  products: Record<string, Product>;
  setSelection: (p: Partial<Selection>) => void;
  dirty: boolean;
}) {
  const sel = draft.selection;
  const packages = settings.packages.filter((p) => p.active);
  const priced = new Map(quote.computed.options.map((o) => [o.id, o]));
  const offered = sel.packageIds.length ? sel.packageIds : packages.map((p) => p.id);

  if (!packages.length) {
    return (
      <div className="card empty">
        <h2>No packages yet</h2>
        <p>Packages are the options you offer (e.g. Good / Better / Best): a range of heat pumps, the cylinders to go with them and a pricing model.</p>
        <Link to="/quotes/settings" className="btn btn-primary">
          Set up packages
        </Link>
      </div>
    );
  }

  return (
    <>
      <section className="card q-adjust">
        <div className="q-row-fields">
          <div className="ed-field">
            <label className="ed-label" htmlFor="q-markup">
              Markup for this quote
            </label>
            <div className="q-unit-input">
              <input
                id="q-markup"
                className="input"
                type="number"
                min={0}
                max={500}
                step={1}
                placeholder="Your pricing model's"
                value={sel.markupOverridePercent ?? ""}
                onChange={(e) => setSelection({ markupOverridePercent: e.target.value === "" ? null : Number(e.target.value) })}
              />
              <span>%</span>
            </div>
            <p className="ed-hint">Replaces the equipment and installation markup (or margin) for every option on this quote.</p>
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="q-disc">
              Discount
            </label>
            <div className="q-inline">
              <select className="input q-narrow" aria-label="Discount type" value={sel.discount.type} onChange={(e) => setSelection({ discount: { ...sel.discount, type: e.target.value as "fixed" | "percent" } })}>
                <option value="fixed">£</option>
                <option value="percent">%</option>
              </select>
              <input id="q-disc" className="input" type="number" min={0} value={sel.discount.value || ""} placeholder="0" onChange={(e) => setSelection({ discount: { ...sel.discount, value: Number(e.target.value) || 0 } })} />
            </div>
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="q-disc-label">
              Discount wording
            </label>
            <input id="q-disc-label" className="input" value={sel.discount.label} placeholder="Discount" maxLength={60} onChange={(e) => setSelection({ discount: { ...sel.discount, label: e.target.value } })} />
          </div>
        </div>
      </section>

      {dirty && <p className="muted q-stale">Prices below are from the last save.</p>}
      <div className={`q-options q-options-${Math.min(packages.length, 3)}`}>
        {packages.map((pkg) => {
          const o = priced.get(pkg.id);
          const on = offered.includes(pkg.id);
          const choice = sel.choices[pkg.id] ?? {};
          return (
            <section key={pkg.id} className={`card q-option${on ? "" : " is-off"}${quote.computed.recommendedId === pkg.id ? " is-rec" : ""}`}>
              <div className="q-option-head">
                <label className="q-check">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) => setSelection({ packageIds: e.target.checked ? [...offered, pkg.id] : offered.filter((x) => x !== pkg.id) })}
                  />
                  Offer
                </label>
                <label className="q-check">
                  <input type="radio" name="recommended" checked={(sel.recommendedId ?? quote.computed.recommendedId) === pkg.id} disabled={!on} onChange={() => setSelection({ recommendedId: pkg.id })} />
                  Recommend
                </label>
              </div>
              <p className="q-tier">{pkg.tier}</p>
              <h2>{pkg.name}</h2>
              <div className="q-pickers">
                <select
                  className="input"
                  aria-label="Heat pump"
                  value={choice.heatPumpId ?? ""}
                  onChange={(e) => setSelection({ choices: { ...sel.choices, [pkg.id]: { ...choice, heatPumpId: e.target.value || undefined } } })}
                >
                  <option value="">Heat pump: smallest that covers it{o?.heatPump ? ` (${o.heatPump.kw} kW)` : ""}</option>
                  {pkg.heatPumpIds
                    .map((id) => products[id])
                    .filter(Boolean)
                    .map((p) => (
                      <option key={p.productId} value={p.productId}>
                        {p.name} ({p.kw} kW{p.designOutputKw && p.designOutputKw !== p.kw ? `, ${p.designOutputKw} kW at design` : ""})
                      </option>
                    ))}
                </select>
                {pkg.cylinderIds.length > 0 && (
                  <select
                    className="input"
                    aria-label="Cylinder"
                    value={choice.cylinderId ?? ""}
                    onChange={(e) => setSelection({ choices: { ...sel.choices, [pkg.id]: { ...choice, cylinderId: e.target.value || undefined } } })}
                  >
                    <option value="">Cylinder: smallest ≥ {quote.computed.requiredCylinderL} L{o?.cylinder ? ` (${o.cylinder.litres} L)` : ""}</option>
                    {pkg.cylinderIds
                      .map((id) => products[id])
                      .filter(Boolean)
                      .map((p) => (
                        <option key={p.productId} value={p.productId}>
                          {p.name} ({p.litres} L)
                        </option>
                      ))}
                  </select>
                )}
              </div>
              {!o ? (
                <p className="muted">{on ? "Save to price this option." : "Not offered on this quote."}</p>
              ) : !o.available ? (
                <div className="flash flash-danger">{o.warnings.join(" ") || "Not available"}</div>
              ) : (
                <OptionPricing o={o} />
              )}
            </section>
          );
        })}
      </div>

      <ExtrasCard settings={settings} sel={sel} setSelection={setSelection} />
      <CustomLinesCard sel={sel} setSelection={setSelection} />
    </>
  );
}

function OptionPricing({ o }: { o: PricedOption }) {
  const t = o.totals!;
  const rc = o.runningCosts;
  const tariff = rc?.tariffs.find((x) => x.id === rc.defaultTariffId) ?? rc?.tariffs[0];
  return (
    <>
      {o.warnings.length > 0 && <div className="flash flash-info">{o.warnings.join(" ")}</div>}
      <table className="q-lines">
        <thead>
          <tr>
            <th>Line</th>
            <th className="num">Cost</th>
            <th className="num">Sell</th>
          </tr>
        </thead>
        <tbody>
          {o.lines.map((l, i) => (
            <tr key={i} className={`q-line-${l.group}`}>
              <td>
                {l.label}
                {l.qty !== 1 && l.group !== "installation" ? ` × ${l.qty}` : ""}
                {l.basis === "per_kw" ? ` (${l.qty} kW)` : l.basis === "per_radiator" ? ` (${l.qty} radiators)` : ""}
                {l.vatRate ? <span className="q-vat"> {l.vatRate}% VAT</span> : null}
              </td>
              <td className="num muted">{l.cost ? gbp(l.cost) : "—"}</td>
              <td className="num">{gbp(l.net)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <dl className="q-totals">
        <Row k="Cost" v={gbp(t.cost)} muted />
        <Row k="Price before VAT" v={gbp(t.net)} />
        <Row k="VAT" v={gbp(t.vat)} />
        {t.grant > 0 && <Row k="Grant" v={`−${gbp(t.grant)}`} />}
        <Row k="Customer pays" v={<strong>{gbp(t.payable)}</strong>} />
        <Row
          k="Profit"
          v={
            <span className={t.marginPercent < 15 ? "q-low-margin" : "q-margin"}>
              {gbp(t.profit)} · {pct(t.marginPercent)} margin · {pct(t.markupPercent)} markup
            </span>
          }
        />
      </dl>
      {rc && tariff && (
        <p className="q-rc-line">
          Running cost {gbp(tariff.annualCost)}/yr on {tariff.name} vs {gbp(rc.current.annualCost)}/yr on {label(FUELS, rc.current.fuel as never).toLowerCase()} (SCOP {rc.scop}).
        </p>
      )}
    </>
  );
}

function Row({ k, v, muted }: { k: string; v: ReactNode; muted?: boolean }) {
  return (
    <div className={muted ? "muted" : undefined}>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </div>
  );
}

function ExtrasCard({ settings, sel, setSelection }: { settings: QuoteSettings; sel: Selection; setSelection: (p: Partial<Selection>) => void }) {
  if (!settings.extras.length) return null;
  const mode = (id: string) => sel.extras.find((x) => x.extraId === id);
  const set = (id: string, patch: Partial<Selection["extras"][number]>) => {
    const cur = mode(id) ?? { extraId: id, mode: "off" as const, qty: null };
    setSelection({ extras: [...sel.extras.filter((x) => x.extraId !== id), { ...cur, ...patch }] });
  };
  return (
    <section className="card">
      <h2>Extras</h2>
      <p className="card-sub">Included extras are in every option's price. Optional ones the customer can add on their quote page.</p>
      <div className="table-scroll">
        <table className="stats-table q-extras">
          <thead>
            <tr>
              <th>Extra</th>
              <th className="num">Price</th>
              <th>On this quote</th>
              <th className="num">Qty</th>
            </tr>
          </thead>
          <tbody>
            {settings.extras.map((e) => {
              const m = mode(e.id);
              return (
                <tr key={e.id}>
                  <td>
                    {e.name}
                    {e.vatRate ? <span className="q-vat"> {e.vatRate}% VAT</span> : null}
                  </td>
                  <td className="num">
                    {gbp(e.unitPrice)}
                    {e.basis === "per_unit" ? ` / ${e.unitLabel || "each"}` : ""}
                  </td>
                  <td>
                    <div className="ed-segmented q-seg-small" role="radiogroup" aria-label={`${e.name} on this quote`}>
                      {(["included", "optional", "off"] as const).map((v) => (
                        <button key={v} type="button" role="radio" aria-checked={(m?.mode ?? "off") === v} className={(m?.mode ?? "off") === v ? "is-on" : ""} onClick={() => set(e.id, { mode: v })}>
                          {v === "included" ? "Included" : v === "optional" ? "Optional" : "Off"}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td className="num">
                    {e.basis === "per_unit" ? (
                      <input
                        className="input q-qty"
                        type="number"
                        min={1}
                        max={e.maxQty}
                        aria-label={`${e.name} quantity`}
                        placeholder={e.qtyFrom === "radiators" ? "radiators" : String(e.defaultQty)}
                        value={m?.qty ?? ""}
                        onChange={(ev) => set(e.id, { qty: ev.target.value ? Number(ev.target.value) : null })}
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CustomLinesCard({ sel, setSelection }: { sel: Selection; setSelection: (p: Partial<Selection>) => void }) {
  const lines = sel.customLines;
  const update = (i: number, patch: Partial<Selection["customLines"][number]>) => setSelection({ customLines: lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  return (
    <section className="card">
      <h2>One-off lines</h2>
      <p className="card-sub">Anything specific to this home (e.g. a longer pipe run, scaffolding, a consumer unit upgrade). Added to every option.</p>
      {lines.length > 0 && (
        <div className="table-scroll">
          <table className="stats-table q-custom">
            <thead>
              <tr>
                <th>Description</th>
                <th className="num">Qty</th>
                <th className="num">Cost each</th>
                <th className="num">Price each</th>
                <th className="num">VAT %</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.id}>
                  <td>
                    <input className="input" aria-label="Description" value={l.label} onChange={(e) => update(i, { label: e.target.value })} />
                  </td>
                  <td>
                    <input className="input q-qty" type="number" min={0.01} aria-label="Quantity" value={l.qty} onChange={(e) => update(i, { qty: Number(e.target.value) || 1 })} />
                  </td>
                  <td>
                    <input className="input q-money" type="number" min={0} aria-label="Cost each" value={l.cost} onChange={(e) => update(i, { cost: Number(e.target.value) || 0 })} />
                  </td>
                  <td>
                    <input className="input q-money" type="number" aria-label="Price each" value={l.price} onChange={(e) => update(i, { price: Number(e.target.value) || 0 })} />
                  </td>
                  <td>
                    <select className="input q-qty" aria-label="VAT rate" value={l.vatRate} onChange={(e) => update(i, { vatRate: Number(e.target.value) })}>
                      <option value={0}>0</option>
                      <option value={5}>5</option>
                      <option value={20}>20</option>
                    </select>
                  </td>
                  <td>
                    <button type="button" className="link-button small" aria-label="Remove line" onClick={() => setSelection({ customLines: lines.filter((_, j) => j !== i) })}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <button type="button" className="btn btn-secondary" onClick={() => setSelection({ customLines: [...lines, { id: `new-${Date.now()}`, label: "", qty: 1, cost: 0, price: 0, vatRate: 0 }] })}>
        <Plus size={16} aria-hidden="true" /> Add a line
      </button>
    </section>
  );
}

// ─── Proposal & send ─────────────────────────────────────────────────────────

function SendTab({
  quote,
  draft,
  settings,
  dirty,
  busy,
  answered,
  setDraft,
  onSend,
  onPreview,
}: {
  quote: Quote;
  draft: Draft;
  settings: QuoteSettings;
  dirty: boolean;
  busy: boolean;
  answered: boolean;
  setDraft: (fn: (d: Draft | null) => Draft | null) => void;
  onSend: (body: { email: boolean; to?: string; subject?: string; message?: string }) => void;
  onPreview: () => void;
}) {
  const [to, setTo] = useState(quote.customer.email);
  const [subject, setSubject] = useState(settings.proposal.emailSubject);
  const [msg, setMsg] = useState(settings.proposal.emailMessage);
  const [copied, setCopied] = useState(false);
  const { access } = useQuoteAccess();
  const pub = quote.publish ?? {};
  const setContent = (patch: Partial<Draft["content"]>) => setDraft((d) => (d ? { ...d, content: { ...d.content, ...patch } } : d));
  const noOptions = !quote.computed.options.some((o) => o.available);

  return (
    <div className="q-grid">
      <section className="card">
        <h2>The proposal</h2>
        <fieldset className="q-fieldset" disabled={answered}>
          <div className="ed-field">
            <label className="ed-label" htmlFor="q-intro">
              Introduction
            </label>
            <textarea id="q-intro" className="input" rows={4} placeholder={settings.proposal.intro} value={draft.content.intro} onChange={(e) => setContent({ intro: e.target.value })} />
            <p className="ed-hint">Leave blank to use the wording in your quote settings.</p>
          </div>
          <div className="q-row-fields">
            <div className="ed-field">
              <label className="ed-label" htmlFor="q-valid">
                Valid until
              </label>
              <input id="q-valid" className="input" type="date" value={draft.content.validUntil} onChange={(e) => setContent({ validUntil: e.target.value })} />
              <p className="ed-hint">Blank: {settings.proposal.validityDays} days from sending.</p>
            </div>
          </div>
          <div className="ed-field">
            <label className="ed-label" htmlFor="q-notes">
              Internal notes (never shown to the customer)
            </label>
            <textarea id="q-notes" className="input" rows={3} value={draft.content.notes} onChange={(e) => setContent({ notes: e.target.value })} />
          </div>
        </fieldset>
        <button type="button" className="btn btn-secondary" onClick={onPreview}>
          <Eye size={16} aria-hidden="true" /> Preview what the customer sees
        </button>
      </section>

      <section className="card">
        <h2>Send</h2>
        {answered ? (
          <p className="muted">This quote has been answered.</p>
        ) : access && !access.termsAccepted ? (
          <QuoteTermsCard />
        ) : (
          <>
            {dirty && <div className="flash flash-info">Save your changes before sending.</div>}
            {noOptions && <div className="flash flash-danger">No option is priced yet: check the heat loss and your packages.</div>}
            <div className="ed-field">
              <label className="ed-label" htmlFor="q-to">
                To
              </label>
              <input id="q-to" className="input" type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="customer@example.com" />
            </div>
            <div className="ed-field">
              <label className="ed-label" htmlFor="q-subject">
                Subject
              </label>
              <input id="q-subject" className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Your heat pump quote from …" />
            </div>
            <div className="ed-field">
              <label className="ed-label" htmlFor="q-msg">
                Message
              </label>
              <textarea id="q-msg" className="input" rows={6} value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Hi …, your quote is ready: compare the options, add any extras and accept it online." />
              <p className="ed-hint">Sent from “your company via xenquote”, with replies going to your company email. The email links to the quote page.</p>
            </div>
            <div className="q-actions">
              <button type="button" className="btn btn-accent" disabled={dirty || busy || noOptions} onClick={() => onSend({ email: true, to, subject, message: msg })}>
                <Mail size={16} aria-hidden="true" /> {pub.sentAt ? "Send again" : "Send quote"}
              </button>
              <button type="button" className="btn btn-secondary" disabled={dirty || busy || noOptions} onClick={() => onSend({ email: false })}>
                Publish the link only
              </button>
            </div>
          </>
        )}

        {pub.url && (
          <div className="q-publish">
            <div className="q-link">
              <code>{pub.url}</code>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  void navigator.clipboard?.writeText(pub.url!).then(() => {
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 1500);
                  })
                }
              >
                <Copy size={14} aria-hidden="true" /> {copied ? "Copied" : "Copy"}
              </button>
              <a className="btn btn-secondary" href={pub.url} target="_blank" rel="noreferrer">
                <ExternalLink size={14} aria-hidden="true" /> Open
              </a>
            </div>
            <dl className="facts">
              <dt>Sent</dt>
              <dd>
                {when(pub.sentAt)}
                {pub.emailed && pub.sentTo ? ` to ${pub.sentTo}` : " (link only)"}
                {pub.sentCount && pub.sentCount > 1 ? ` · ${pub.sentCount} times` : ""}
              </dd>
              <dt>Opened</dt>
              <dd>{pub.views ? `${pub.views} time${pub.views === 1 ? "" : "s"}, first ${when(pub.viewedAt)}, last ${when(pub.lastViewedAt)}` : "Not yet"}</dd>
              <dt>Notifications</dt>
              <dd>{pub.notifyEmails?.length ? pub.notifyEmails.join(", ") : "—"}</dd>
            </dl>
          </div>
        )}
      </section>
    </div>
  );
}
